import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Document, GetDocumentsPayload, GetTagsPayload, OrderDocumentsService, Tag } from 'app/shared/services/order-documents/order-documents.service';
import { DialogUploadDocumentComponent } from './dialog-upload-document/dialog-upload-document.component';
import { MatTableDataSource } from '@angular/material/table';
import { DialogEditDocumentComponent } from './dialog-edit-document/dialog-edit-document/dialog-edit-document.component';
import { DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { DOCUMENT_STATUS_MAP, DocumentStatusValue } from 'app/shared/helpers/document-status-map.helper';
import { TagsAutocompleteOptionsPartial } from 'app/shared/components/tags-autocomplete/tags-autocomplete/tags-autocomplete.component';
import { DialogQrCodeUploadComponent, QRCodeUploadPayload } from './dialog-qr-code-upload/dialog-qr-code-upload.component';
import moment from 'moment-timezone';
import { PAPER_SCRIPT, UPLOADED_VIA_QR_CODE } from 'app/shared/constants';
import { tap } from 'rxjs/operators';

export interface DocumentDialogData {
  userId: number;
  orderId: number;
  tags: Tag[];
  document?: Document;
  options: OrderDocumentsConfig;
  defaultTagIds?: number[];
  defaultTitle?: string;
}

export type DocumentActionMode = 'normal' | 'readonly' | 'uploadOnly';

export interface OrderDocumentsConfig {
  tagsAutocompleteOptions: TagsAutocompleteOptionsPartial;
  showExpiryDateColumn: boolean;
  restrictTagsByProfileId: boolean;
  enableDocumentUpload: boolean;
  enableQRCodeUpload: boolean;
  enablePaperScriptsMode: boolean;
  disableTagAutocompleteInUploadDialog: boolean;
  disableTagAutocompleteInEditDialog: boolean;
  showOnlyOrderDocument: boolean;
  disableDocumentTitleInUploadDialog: boolean;
  disableDocumentTitleInEditDialog: boolean;
  actionMode: DocumentActionMode;
}

export type OrderDocumentsConfigPartial = Partial<OrderDocumentsConfig>;

export const DEFAULT_ORDER_DOCUMENTS_CONFIG: OrderDocumentsConfig = {
  tagsAutocompleteOptions: {},
  showExpiryDateColumn: false,
  restrictTagsByProfileId: false,
  enableDocumentUpload: true,
  showOnlyOrderDocument: false,
  enableQRCodeUpload: true,
  enablePaperScriptsMode: false,
  disableTagAutocompleteInUploadDialog: false,
  disableTagAutocompleteInEditDialog: false,
  disableDocumentTitleInUploadDialog: false,
  disableDocumentTitleInEditDialog: false,
  actionMode: 'normal'
};

@Component({
  selector: 'app-order-documents',
  templateUrl: './order-documents.component.html',
  styleUrls: ['./order-documents.component.scss']
})
export class OrderDocumentsComponent implements OnInit {
  @Input() userId: number = null;
  @Input() orderId: number = null;
  @Input() profileTypeId: number = null;
  @Input() uniqueOrderId: number = null;
  @Input() email: string = null;
  @Input() config: OrderDocumentsConfigPartial = {};
  @Input() defaultTags: string[] = [];
  @Input() addDocumentButtonLabel: string = 'Add Document';
  @Input() defaultDocumentTitle: string = '';
  @Output() medicalCertificateExists: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() documentCountChange: EventEmitter<number> = new EventEmitter<number>();


  tags: Tag[] = [];
  defaultTagIds: number[] = [];
  loading: boolean = false;
  options: OrderDocumentsConfig = { ...DEFAULT_ORDER_DOCUMENTS_CONFIG, ...this.config };

  displayedColumns = [
    'date',
    'name',
    'type',
    'status',
    'action',
  ];
  dataSource: MatTableDataSource<Document> = new MatTableDataSource<Document>();
  defaultTagIdsMap: Map<string, number>;

  constructor(private documentService: OrderDocumentsService,
    private dialog: MatDialog,
    private alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this.getDocumentsAndTags();
    this.updateDisplayedColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.updateOptions();
    }
  }

  private updateOptions(): void {
    const baseOptions = { ...DEFAULT_ORDER_DOCUMENTS_CONFIG, ...this.config };

    this.options = this.config.actionMode === 'readonly'
      ? { ...baseOptions, enableDocumentUpload: false, enableQRCodeUpload: false }
      : baseOptions;
  }

  private updateDisplayedColumns(): void {
    if (this.options.showExpiryDateColumn) {
      const position = this.displayedColumns.length - 1;
      this.displayedColumns.splice(position, 0, 'expiryDate');
    }
  }

  getDocumentsAndTags() {
    this.getDefaultTags().subscribe(() => {
      this.getDocuments();
      this.getTags();
    });
  }

  getDocuments() {
    this.loading = true;
    const data: GetDocumentsPayload = {
      user_id: this.userId
    }
    if (this.orderId) {
      data.order_id = this.orderId;
    }
    if (this.options.enablePaperScriptsMode) {
      data['filters[tag_id]'] = this.defaultTagIdsMap.get(PAPER_SCRIPT);
      data['sort_by[created_date]'] = 'desc';
    }
    this.documentService.getDocuments(data).subscribe((response) => {
      this.loading = false;
      if (this.options.showOnlyOrderDocument) {
        this.dataSource.data = response.documents.filter((doc: Document) => {
          return doc.title?.toLowerCase().includes('order document')
        })
      } else {
        this.dataSource.data = response.documents;
      }
      this.documentCountChange.emit(this.dataSource.data.length);
      const medicalCertificateExists = response.documents.some((doc) =>
        doc.tags.some((tag) => tag.name === "Medical Certificate")
      );
      this.medicalCertificateExists.emit(medicalCertificateExists);
    }, () => {
      this.loading = false;
    });
  }

  getTags() {
    let data: GetTagsPayload = {
      user_id: this.userId,
    }
    if (this.orderId) {
      data.order_id = this.orderId;
    }
    if (this.options.restrictTagsByProfileId && this.profileTypeId) {
      data = {
        profile_type_id: this.profileTypeId,
        get_defaults: 0
      }
    }
    this.documentService.getTags(data).subscribe((response) => {
      this.tags = response.tags;
    });
  }

  getDefaultTags() {
    const tagNames = [UPLOADED_VIA_QR_CODE, ...this.defaultTags];

    return this.documentService.getTagIdsByNames(tagNames).pipe(
      tap(originalTagMap => {
        this.defaultTagIdsMap = new Map(originalTagMap);

        const filteredMap = new Map(originalTagMap);
        filteredMap.delete(UPLOADED_VIA_QR_CODE);

        this.defaultTagIds = Array.from(filteredMap.values());
      })
    );
  }

  openUploadDocumentDialog() {
    const dialogRef = this.dialog.open(DialogUploadDocumentComponent, {
      width: '400px',
      data: {
        userId: this.userId,
        orderId: this.orderId,
        tags: this.tags,
        options: this.options,
        defaultTagIds: this.defaultTagIds,
        defaultTitle: this.defaultDocumentTitle
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.getDocumentsAndTags();
      }
    });
  }

  openEditDocumentDialog(document: Document) {
    const dialogRef = this.dialog.open(DialogEditDocumentComponent, {
      width: '400px',
      data: {
        userId: this.userId,
        orderId: this.orderId,
        tags: this.tags,
        document,
        options: this.options
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.getDocumentsAndTags();
      }
    });
  }

  openDeleteDocumentConfirmDialog(document: Document) {
    const dialogRef = this.dialog.open(DialogConfirmationComponent, {
      width: '400px',
      data: {
        headerText: 'Delete Document?',
        message: `Are you sure you want to delete ${document.title}?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {

        this.documentService.deleteDocument(document).subscribe(() => {
          this.alertService.notifySuccess('Document deleted successfully');
          this.getDocuments();
        },
          () => {
            this.alertService.notifyError('Failed to delete the document. Please try again later.');
          })
      }
    });
  }

  openRestoreDocumentConfirmDialog(document: Document) {
    const dialogRef = this.dialog.open(DialogConfirmationComponent, {
      width: '400px',
      data: {
        headerText: 'Restore Document?',
        message: `Are you sure you want to restore ${document.title}?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const data = {
          tags: document.tags.map(tag => tag.tag_id).join(','),
          user_id: this.userId,
          document_id: document.document_id,
          order_id: this.orderId,
          status: 1
        }
        this.documentService.updateDocument(data).subscribe(() => {
          this.alertService.notifySuccess('Document restored successfully');
          this.getDocuments();
        },
          () => {
            this.alertService.notifyError('Failed to restore the document. Please try again later.');
          })
      }
    });
  }

  downloadDocument(url: string) {
    window.open(url, '_blank')
  }

  getDocumentStatusLabel(status: Document['status']): DocumentStatusValue {
    return DOCUMENT_STATUS_MAP[status];
  }

  openQRCodeUploadDialog(): void {
    const expiryTime = moment().add(30, 'minutes').toISOString();
    const tag_ids = [this.defaultTagIdsMap.get(UPLOADED_VIA_QR_CODE), ...this.defaultTagIds];
    const payload: QRCodeUploadPayload = {
      order_id: this.orderId,
      user_id: this.userId,
      expiry_time: expiryTime,
      unique_order_id: this.uniqueOrderId,
      email: this.email,
      tag_ids,
      file_name: this.defaultDocumentTitle
    };

    const filteredPayload = Object.keys(payload).reduce((acc: any, key) => {
      const value = payload[key];
      if (value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const dialogRef = this.dialog.open(DialogQrCodeUploadComponent, {
      width: '420px',
      disableClose: true,
      data: {
        payload: filteredPayload
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getDocuments();
      }
    })
  }

  public refreshDocuments(): void {
    this.getDocumentsAndTags();
  }
}
